import * as React from 'react'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import Link from 'next/link'

import * as url from '@bob/url'
import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'

import { Appear } from './Appear'

export type WhoProps = React.HTMLAttributes<HTMLDivElement>

const bulletPoints = [
  'Communiquer en toute transparence auprès de leurs clients et de leurs partenaires.',
  "Utiliser des matières premières durables, plus respectueuses de l'environnement, en minimisant l'utilisation de ressources non renouvelables et en encourageant l'utilisation de matériaux recyclés ou upcyclés.",
  'S’assurer que les droits fondamentaux des travailleurs sont bien respectés tout au long de la chaîne de production de leurs produits.',
  'Limiter au maximum la surproduction en trouvant un juste équilibre entre offre et demande (par exemple : opter pour un système de prévente, analyser et revoir les prévisions en fonction des ventes précédentes, lancer de nouveaux produits avec un rythme raisonné etc.)',
  'Réduire leur impact carbone en adoptant des pratiques de production durables et en favorisant l’utilisation des énergies renouvelables.',
  'Proposer le prix le plus juste possible pour tous les acteurs de la chaîne de production et pour les consommatrices et consommateurs.',
]

function WhoComponent({ ...rest }: WhoProps): React.ReactElement {
  const [$ref, inView] = useInView({
    threshold: 0.0,
    rootMargin: `0px 0px -40% 0px`,
    initialInView: false,
    triggerOnce: true,
  })

  return (
    <WhoSection {...rest} ref={$ref}>
      <HeroContainer>
        <Wrapper>
          <Appear isVisible={inView}>
            <S.heading.Title2>Principes essentiels</S.heading.Title2>
          </Appear>
          <Appear isVisible={inView} delay={0.3} hasTranslate={false}>
            <BlobSticker color="light-green">
              <ds.stickers.criteria.RespectfulProduct />
            </BlobSticker>
          </Appear>
        </Wrapper>
        <S.copy.Text5>
          <Appear isVisible={inView} delay={0.6}>
            <p>
              Les marques signataires de la charte Bien ou Bien sont dans une démarche d’amélioration continue visant à
              avoir un meilleur impact sur les gens ET l’environnement. Elles s’engagent à :
            </p>
          </Appear>
        </S.copy.Text5>
      </HeroContainer>
      <Appear isVisible={inView} delay={1}>
        <ListContainer>
          <ul>
            {bulletPoints.map((bulletPoint, index) => (
              <li key={index}>
                <TextWithBlob>
                  <ds.BlobBackground variant={2} color="light-green">
                    <S.heading.Title3 as="div">{index + 1}</S.heading.Title3>
                  </ds.BlobBackground>
                  <S.copy.Text5>{bulletPoint}</S.copy.Text5>
                </TextWithBlob>
              </li>
            ))}
          </ul>
        </ListContainer>
        <LinkWrapper>
          <Link prefetch={false} href={url.brands({ bobChart: 'signatory' }).path} passHref legacyBehavior>
            <ds.ButtonAsLink autoSized>Liste des marques signataires de la charte Bien ou Bien</ds.ButtonAsLink>
          </Link>
        </LinkWrapper>
      </Appear>
    </WhoSection>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 32px;

  @media screen and (${ds.breakpoint.LAPTOP}) {
    display: block;
    margin-bottom: 0;
  }
`

const TextWithBlob = styled.div`
  display: flex;

  ${ds.BlobBackground} {
    height: 40px;
    margin-right: 20px;
    position: relative;
  }

  ${S.heading.Title3} {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 40px;
  }

  @media not screen and (${ds.breakpoint.LAPTOP}) {
    ${S.heading.Title3} {
      font-size: 20px; // HACK: irregular font manipulation
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    ${ds.BlobBackground} {
      height: 70px;
      top: -8px;
    }

    ${S.heading.Title3} {
      height: 70px;
      width: 70px;
    }
  }
`

const ListContainer = styled(ds.Container)`
  ul {
    margin-top: 80px;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
  }

  li {
    grid-column: 1 / span 12;
    margin-bottom: 20px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    li {
      grid-column: 3 / span 9;
    }
  }
`

export const Who = styled(WhoComponent)`
  ${S.copy.Text5} {
    font-size: 18px; // HACK: Irregular font manipulation
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${S.copy.Text5} {
      font-size: 24px; // HACK: Irregular font manipulation
      max-width: 800px;
    }
  }
`

const WhoSection = styled.section`
  position: relative;
  overflow: hidden;
  background: ${ds.color.PINK};
  padding-top: 66px;
  padding-bottom: 25px;

  ${ds.Button} {
    margin-top: 40px;
    margin-bottom: 67px;
    margin-left: auto;
    margin-right: auto;
    display: block;
    max-width: 330px;
  }

  ${S.copy.Text5} {
    p {
      margin: 1.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    margin-top: 100px;
    margin-bottom: 100px;
    padding-top: 140px;

    ${ds.Button} {
      max-width: none;
    }
  }
`

const BlobSticker = styled(ds.BlobBackground)`
  position: relative;
  display: block;
  width: 100%;
  max-width: 339px;
  margin: 0 auto;

  &:before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  ${ds.stickers.Sticker} {
    position: absolute;
    width: ${(240 / 339) * 100}%;
    height: ${(240 / 339) * 100}%;
    top: ${(10 / 339) * 100}%;
    left: ${(41 / 339) * 100}%;
    background: none;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 30px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    margin-top: 0;
  }
`

const HeroContainer = styled(ds.Container)`
  display: grid;
  gap: 0px 20px;

  ${S.heading.Title2} {
    text-align: center;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    gap: 30px 20px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    grid-template-columns: repeat(12, 1fr);

    & > div:first-child {
      position: relative;
      grid-column: 2 / span 7;
    }

    ${BlobSticker} {
      position: absolute;
      top: -100px;
      left: 100%;
      margin-left: 35px;
    }

    ${S.heading.Title2} {
      text-align: left;
      padding-right: 50px;
    }

    ${S.copy.Text5} {
      grid-column: 3 / span 7;
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    gap: 66px 36px;
  }
`

const LinkWrapper = styled.div`
  text-align: center;
  margin-top: 40px;
  margin-bottom: 67px;
`
